import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

export default function FlujoCaja() {
  const [periodo, setPeriodo] = useState("mensual");
  const [fechaInicio, setFechaInicio] = useState("2024-11-01");
  const [fechaFin, setFechaFin] = useState("2024-11-30");

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    saldoInicial: 0,
    actividadesOperacion: { entradas: [], salidas: [] },
    actividadesInversion: { entradas: [], salidas: [] },
    actividadesFinanciamiento: { entradas: [], salidas: [] },
    aumentoDisminucion: 0,
    saldoFinal: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_ENDPOINTS.CONTABILIDAD.FLUJO_CAJA, {
          params: {
            periodo,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
          },
        });
        setData(response.data);
      } catch (error) {
        console.error("Error al cargar flujo de caja:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [periodo, fechaInicio, fechaFin]);

  const {
    saldoInicial,
    actividadesOperacion,
    actividadesInversion,
    actividadesFinanciamiento,
    aumentoDisminucion,
    saldoFinal,
  } = data;

  // Cálculos totales
  const flujoOperacion =
    actividadesOperacion.entradas.reduce((sum, item) => sum + item.monto, 0) +
    actividadesOperacion.salidas.reduce((sum, item) => sum + item.monto, 0);

  const flujoInversion =
    actividadesInversion.entradas.reduce((sum, item) => sum + item.monto, 0) +
    actividadesInversion.salidas.reduce((sum, item) => sum + item.monto, 0);

  const flujoFinanciamiento =
    actividadesFinanciamiento.entradas.reduce((sum, item) => sum + item.monto, 0) +
    actividadesFinanciamiento.salidas.reduce((sum, item) => sum + item.monto, 0);

  const LineaItem = ({ item, nivel = 0 }) => (
    <MDBox display="flex" justifyContent="space-between" py={0.5} px={3} ml={nivel * 2}>
      <MDTypography variant="button" fontWeight="regular">
        {item.concepto}
      </MDTypography>
      <MDTypography
        variant="button"
        fontWeight="medium"
        color={item.monto >= 0 ? "success" : "error"}
      >
        {formatCurrency(item.monto)}
      </MDTypography>
    </MDBox>
  );

  LineaItem.propTypes = {
    item: PropTypes.shape({
      concepto: PropTypes.string.isRequired,
      monto: PropTypes.number.isRequired,
    }).isRequired,
    nivel: PropTypes.number,
  };

  const SubTotal = ({ label, monto, color = "text", nivel = 0 }) => (
    <MDBox
      display="flex"
      justifyContent="space-between"
      py={1}
      px={3}
      ml={nivel * 2}
      sx={{ bgcolor: "grey.100" }}
    >
      <MDTypography variant="button" fontWeight="bold" color={color}>
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="bold" color={color}>
        {formatCurrency(monto)}
      </MDTypography>
    </MDBox>
  );

  SubTotal.propTypes = {
    label: PropTypes.string.isRequired,
    monto: PropTypes.number.isRequired,
    color: PropTypes.string,
    nivel: PropTypes.number,
  };

  const Total = ({ label, monto, color = "info" }) => (
    <MDBox
      display="flex"
      justifyContent="space-between"
      py={1.5}
      px={3}
      sx={{ bgcolor: `${color}.main` }}
    >
      <MDTypography variant="h6" fontWeight="bold" color="white">
        {label}
      </MDTypography>
      <MDTypography variant="h6" fontWeight="bold" color="white">
        {formatCurrency(monto)}
      </MDTypography>
    </MDBox>
  );

  Total.propTypes = {
    label: PropTypes.string.isRequired,
    monto: PropTypes.number.isRequired,
    color: PropTypes.string,
  };

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  Estado de Flujo de Efectivo
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <TextField
                    select
                    label="Periodo"
                    value={periodo}
                    onChange={(e) => setPeriodo(e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="mensual">Mensual</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="anual">Anual</option>
                  </TextField>
                  <TextField
                    type="date"
                    label="Fecha Inicio"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="Fecha Fin"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <MDButton variant="gradient" color="info">
                    <Icon>print</Icon>&nbsp;Imprimir
                  </MDButton>
                </MDBox>
              </MDBox>

              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Card variant="outlined">
                    {/* Saldo Inicial */}
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      py={2}
                      px={3}
                      sx={{ bgcolor: "primary.main" }}
                    >
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        Saldo Inicial de Efectivo
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        {formatCurrency(saldoInicial)}
                      </MDTypography>
                    </MDBox>

                    {/* Actividades de Operación */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="success">
                          ACTIVIDADES DE OPERACIÓN
                        </MDTypography>
                      </MDBox>
                      <MDBox px={3} mt={1} mb={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">
                          Entradas de Efectivo
                        </MDTypography>
                      </MDBox>
                      {actividadesOperacion.entradas.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={1} />
                      ))}
                      <MDBox px={3} mt={2} mb={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">
                          Salidas de Efectivo
                        </MDTypography>
                      </MDBox>
                      {actividadesOperacion.salidas.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={1} />
                      ))}
                      <Total
                        label="Flujo Neto de Actividades de Operación"
                        monto={flujoOperacion}
                        color={flujoOperacion >= 0 ? "success" : "error"}
                      />
                    </MDBox>

                    {/* Actividades de Inversión */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="warning">
                          ACTIVIDADES DE INVERSIÓN
                        </MDTypography>
                      </MDBox>
                      {actividadesInversion.entradas.length > 0 && (
                        <>
                          <MDBox px={3} mt={1} mb={0.5}>
                            <MDTypography variant="caption" fontWeight="bold" color="text">
                              Entradas de Efectivo
                            </MDTypography>
                          </MDBox>
                          {actividadesInversion.entradas.map((item, index) => (
                            <LineaItem key={index} item={item} nivel={1} />
                          ))}
                        </>
                      )}
                      <MDBox px={3} mt={actividadesInversion.entradas.length > 0 ? 2 : 1} mb={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">
                          Salidas de Efectivo
                        </MDTypography>
                      </MDBox>
                      {actividadesInversion.salidas.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={1} />
                      ))}
                      <Total
                        label="Flujo Neto de Actividades de Inversión"
                        monto={flujoInversion}
                        color={flujoInversion >= 0 ? "success" : "warning"}
                      />
                    </MDBox>

                    {/* Actividades de Financiamiento */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="info">
                          ACTIVIDADES DE FINANCIAMIENTO
                        </MDTypography>
                      </MDBox>
                      <MDBox px={3} mt={1} mb={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">
                          Entradas de Efectivo
                        </MDTypography>
                      </MDBox>
                      {actividadesFinanciamiento.entradas.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={1} />
                      ))}
                      <MDBox px={3} mt={2} mb={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">
                          Salidas de Efectivo
                        </MDTypography>
                      </MDBox>
                      {actividadesFinanciamiento.salidas.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={1} />
                      ))}
                      <Total
                        label="Flujo Neto de Actividades de Financiamiento"
                        monto={flujoFinanciamiento}
                        color={flujoFinanciamiento >= 0 ? "success" : "info"}
                      />
                    </MDBox>

                    {/* Resumen */}
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      py={1.5}
                      px={3}
                      sx={{ bgcolor: "grey.200" }}
                    >
                      <MDTypography variant="h6" fontWeight="bold">
                        Aumento (Disminución) Neto en Efectivo
                      </MDTypography>
                      <MDTypography
                        variant="h6"
                        fontWeight="bold"
                        color={aumentoDisminucion >= 0 ? "success" : "error"}
                      >
                        {formatCurrency(aumentoDisminucion)}
                      </MDTypography>
                    </MDBox>

                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      py={2}
                      px={3}
                      sx={{ bgcolor: "primary.main" }}
                    >
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        Saldo Final de Efectivo
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        {formatCurrency(saldoFinal)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>

                {/* Panel de Análisis */}
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <MDBox p={2}>
                      <MDTypography variant="h6" fontWeight="medium" mb={2}>
                        Resumen de Flujos
                      </MDTypography>
                      <MDBox mb={2} p={2} sx={{ bgcolor: "success.lighter", borderRadius: 2 }}>
                        <MDTypography variant="caption" color="text">
                          Operación
                        </MDTypography>
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          color={flujoOperacion >= 0 ? "success" : "error"}
                        >
                          {formatCurrency(flujoOperacion)}
                        </MDTypography>
                      </MDBox>
                      <MDBox mb={2} p={2} sx={{ bgcolor: "warning.lighter", borderRadius: 2 }}>
                        <MDTypography variant="caption" color="text">
                          Inversión
                        </MDTypography>
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          color={flujoInversion >= 0 ? "success" : "warning"}
                        >
                          {formatCurrency(flujoInversion)}
                        </MDTypography>
                      </MDBox>
                      <MDBox p={2} sx={{ bgcolor: "info.lighter", borderRadius: 2 }}>
                        <MDTypography variant="caption" color="text">
                          Financiamiento
                        </MDTypography>
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          color={flujoFinanciamiento >= 0 ? "success" : "info"}
                        >
                          {formatCurrency(flujoFinanciamiento)}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Card>

                  <Card variant="outlined">
                    <MDBox p={2}>
                      <MDTypography variant="h6" fontWeight="medium" mb={2}>
                        Análisis
                      </MDTypography>
                      <MDBox mb={2}>
                        <MDTypography variant="caption" color="text">
                          Variación de Efectivo
                        </MDTypography>
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          color={aumentoDisminucion >= 0 ? "success" : "error"}
                        >
                          {((aumentoDisminucion / saldoInicial) * 100).toFixed(2)}%
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="caption" color="text">
                          Estado del Flujo
                        </MDTypography>
                        <MDTypography
                          variant="h6"
                          fontWeight="bold"
                          color={flujoOperacion >= 0 ? "success" : "error"}
                        >
                          {flujoOperacion >= 0 ? "Positivo ✓" : "Negativo ✗"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
